import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Form, Spin } from 'antd';
import {
  downloadFileUrl,
  generateFileUrl,
  getValueDefault,
  maxRepresentative,
  openFile,
  validateEmail,
} from '../../utilities/Functions';

import {
  companyProperty,
  personType,
  REPRESENTATIVE,
  validationType,
} from '../../utilities/Constant';
import CustomIcon from '../CustomIcon/CustomIcon';

import RepresentativeCardEdit from '../RepresentativeCardEdit/RepresentativeCardEdit';
import { requestProperty } from '../../templates/NewRequest/constants';
import PTextInput from '../PTextInput';

import './index.less';
import FileItem from '../FilteItem/FileItem';
import WUploadFile from '../UploadFile';
import { KB_1 } from '../DropZone/DropZone';

const { Title, Text } = Typography;

const CompanyCardEdit = ({
  company,
  className = '',
  title = '',
  onSetCompanies = () => {},
  companyIndex,
  isReadOnly,
  counterPart,
  onSetRequest,
  errorMessages,
  form,
}) => {
  const {
    address = '',
    electronicCertificate = '',
    registerOffice = '',
    ruc = '',
    socialReason = '',
    legalRepresentatives,
    email,
    rucFile,
  } = company;

  const [loading, setLoading] = useState(false);
  const errorUpload = errorMessages && !rucFile;

  const handleUpload = async (option) => {
    try {
      const { onSuccess, file } = option;

      if (file) {
        const { filename, id, url } = await generateFileUrl(file);
        const value = {
          url,
          filename,
          size: Math.round(file.size / KB_1),
          type: file.type,
          id,
        };
        onChangeValue(value, companyProperty.RUC_FILE);
        onSuccess();
      }
    } catch (e) {
      console.log(e);
    }
  };
  const onUploadFile = async (params) => {
    setLoading(true);
    await handleUpload(params);
    setLoading(false);
  };

  const [editItemRepresentative, setEditRepresentative] = useState(null);

  const checkEmail = () => validateEmail(getValueDefault(email));

  const onChangeValue = (value, property) => {
    const companyToUpdate = { ...company };
    companyToUpdate[property] = value;
    onSetCompanies(companyToUpdate, companyIndex);
  };

  const onSetRepresentative = (representative, representativeIndex) => {
    const companyToUpdate = { ...company };
    companyToUpdate.legalRepresentatives[representativeIndex] = representative;
    onSetCompanies(companyToUpdate, companyIndex);
  };

  const addRepresentativeToCompany = () => {
    const counterPartToUpdate = [...counterPart];
    const indexLegal = counterPart.findIndex((e) => e.type === personType.LEGAL);

    const legalPerson = counterPartToUpdate[indexLegal];
    const quantity = legalPerson.companies[companyIndex].lastRepresentative + 1;
    const representative = `${REPRESENTATIVE} ${quantity}`;
    counterPartToUpdate[indexLegal].companies[companyIndex][
      companyProperty.LEGAL_REPRESENTATIVES
    ].push({
      representative,
    });
    counterPartToUpdate[indexLegal].companies[companyIndex].lastRepresentative = quantity;
    onSetRequest(requestProperty.COUNTER_PART, counterPartToUpdate);
    setEditRepresentative(representative);
  };

  const editRepresentantive = (representative) => {
    const currentRepresentative = representative.representative;
    if (editItemRepresentative === currentRepresentative) setEditRepresentative(null);
    else setEditRepresentative(currentRepresentative);
  };

  const onDeleteRepresentativeFromCompany = (representantive) => {
    setEditRepresentative(null);
    onDeleteRepresentative(representantive?.representative);
  };

  const onDeleteRepresentative = (representative) => {
    const counterPartToUpdate = [...counterPart];
    let representativesList;
    // legal person
    const indexLegal = counterPart.findIndex((e) => e.type === personType.LEGAL);
    representativesList =
      counterPartToUpdate[indexLegal].companies[companyIndex][
        companyProperty.LEGAL_REPRESENTATIVES
      ];
    representativesList = representativesList.filter((re) => re.representative !== representative);
    counterPartToUpdate[indexLegal].companies[companyIndex][companyProperty.LEGAL_REPRESENTATIVES] =
      representativesList;
    const repQuantity = representativesList.length;
    if (repQuantity === 1) {
      counterPartToUpdate[indexLegal].companies[companyIndex].lastRepresentative = 1;
    } else {
      counterPartToUpdate[indexLegal].companies[companyIndex].lastRepresentative =
        maxRepresentative(representativesList);
    }
    onSetRequest(requestProperty.COUNTER_PART, counterPartToUpdate);
  };

  useEffect(() => {
    if (errorMessages) {
      form.validateFields();
    }
  }, [editItemRepresentative]);

  useEffect(() => {
    if (legalRepresentatives?.length === 1) {
      setEditRepresentative(legalRepresentatives[0].representative);
    }
  }, [company]);

  return (
    <div className={`company-card-container ${className}`}>
      <Title className="company-card-title">{title}</Title>
      <Row gutter={10} className="company-card-row company-firstSection">
        <Col>
          <Form.Item
            name={`${companyProperty.RUC}${companyIndex}`}
            rules={[{ required: true, message: 'Ingrese número de RUC' }]}
          >
            <PTextInput
              placeholder="Número de Ruc"
              value={getValueDefault(ruc)}
              onChange={(value) => onChangeValue(value, companyProperty.RUC)}
              type="number"
              maxLength={11}
              validation={validationType.RUC_LEGAL}
              readOnly={isReadOnly}
              errorMessage={`${
                errorMessages && getValueDefault(ruc) ? 'RUC jurídico inválido' : ''
              }`}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={`${companyProperty.SOCIAL_REASON}${companyIndex}`}
            rules={[{ required: true, message: 'Ingrese razón social' }]}
          >
            <PTextInput
              placeholder="Razón social"
              value={getValueDefault(socialReason)}
              onChange={(value) => onChangeValue(value, companyProperty.SOCIAL_REASON)}
              readOnly={isReadOnly}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={`${companyProperty.ELECTRONIC_CERTIFICATE}${companyIndex}`}
            rules={[{ required: true, message: 'Ingrese partida Electrónica' }]}
          >
            <PTextInput
              placeholder="Partida Electrónica"
              value={getValueDefault(electronicCertificate)}
              onChange={(value) => onChangeValue(value, companyProperty.ELECTRONIC_CERTIFICATE)}
              readOnly={isReadOnly}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={`${companyProperty.ADDRESS}${companyIndex}`}
            rules={[{ required: true, message: 'Ingrese domicilio Fiscal' }]}
          >
            <PTextInput
              placeholder="Domicilio Fiscal"
              value={getValueDefault(address)}
              onChange={(value) => onChangeValue(value, companyProperty.ADDRESS)}
              readOnly={isReadOnly}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={`${companyProperty.REGISTER_OFFICE}${companyIndex}`}
            rules={[{ required: true, message: 'Ingrese sede de la Oficina Registral' }]}
          >
            <PTextInput
              placeholder="Sede de la Oficina Registral"
              value={getValueDefault(registerOffice)}
              onChange={(value) => onChangeValue(value, companyProperty.REGISTER_OFFICE)}
              readOnly={isReadOnly}
            />
          </Form.Item>
        </Col>
        <Col>
          <Form.Item
            name={`${companyProperty.EMAIL}${companyIndex}`}
            rules={[{ required: true, validator: checkEmail }]}
          >
            <PTextInput
              placeholder="Correo de contacto"
              value={getValueDefault(email)}
              onChange={(value) => onChangeValue(value, companyProperty.EMAIL)}
              readOnly={isReadOnly}
              lowerCase
            />
          </Form.Item>
        </Col>
        {!isReadOnly && (
          <Col className="item--large">
            <div className="row-upload-powerVigency">
              <div className="upload-file">
                <WUploadFile
                  title="Vigencia de poder"
                  handleUpload={onUploadFile}
                  icon={<CustomIcon type="uploadFile" />}
                  errorMessage={errorUpload && 'Debe seleccionar un archivo'}
                />
              </div>

              {loading ? (
                <div>
                  <Spin size="small" />
                  <Text> Subiendo archivo</Text>
                </div>
              ) : (
                rucFile?.filename && (
                  <div className="filename-powerVigency">
                    <Row onClick={() => openFile(rucFile)}>
                      <Text className="fileItem-text">{rucFile?.filename}</Text>
                      <Col flex="0 0 26px">
                        <CustomIcon type="downloadFile" />
                      </Col>
                    </Row>
                  </div>
                )
              )}
            </div>
          </Col>
        )}
        {rucFile && isReadOnly && (
          <Col>
            <div className="justification-filesContainerColOne">
              <div>
                <FileItem file={rucFile} download={downloadFileUrl} />
              </div>
            </div>
          </Col>
        )}
      </Row>
      <div className="container-representantive">
        <div className="titleContainer">
          <Title className="title">Información de representantes</Title>
        </div>
        <Text className="subtitleContainer">
          <CustomIcon className="subtitleIcon" type="ic_alert_withoutBackg" />
          Es obligatorio completar todo los campos.
        </Text>
        {legalRepresentatives.map((rep, indexRep) => (
          <RepresentativeCardEdit
            key={`${personType.LEGAL}${rep.representative}`}
            className="detailRequest-containerContent-with-border"
            title={rep.representative}
            representative={rep}
            selectedIndexRepresentative={indexRep}
            onSetRepresentative={onSetRepresentative}
            isReadOnly={isReadOnly}
            showDetail={editItemRepresentative === rep.representative}
            editRepresentantive={editRepresentantive}
            onDeleteRepresentative={onDeleteRepresentativeFromCompany}
            showDelete={legalRepresentatives.length > 1}
            companyIndex={companyIndex}
            person={personType.LEGAL}
            errorMessages={errorMessages}
          />
        ))}
        {!isReadOnly && (
          <button
            type="button"
            className="buttonAddRepresentative"
            onClick={addRepresentativeToCompany}
          >
            Agregar representante
            <CustomIcon type="addCircle" />
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanyCardEdit;
